import type { PartialOnly } from '@ipfabric/utils';

import type { SelectOptionType } from 'components/Select/SelectOption';

/**
 * Shortcut to create select options from string values and localized labels
 *
 * Options should be an array with fixed defined order (which object does not have),
 * so using just plain object from locale is not a solution. On the other hand,
 * putting array of select options into locale would be a bad pattern (shape of
 * select options input is not a concern of locale). So, there is this utility to
 * concisely define such select options.
 *
 * @example
 * const labels = {
 *   bar: 'The Bar',
 *   baz: 'The Baz',
 *   foo: 'The Foo',
 * };
 *
 * // basic
 * createSelectOptions(['foo', 'bar', 'baz'], labels);
 *
 * // customize other option stuff
 * createSelectOptions(
 *  [
 *   'foo',
 *   'bar',
 *   // you can define full option object, except label is optional as it is taken from `labels`
 *   {value: 'baz', disabled: true, tooltip: 'Cannot baz at the moment.'},
 *  ],
 *  labels,
 * );
 */
export const createSelectOptions = <TValue extends string>(
  values: readonly // plain string
  (
    | TValue
    // set other SelectOptionType's attributes
    | PartialOnly<SelectOptionType<TValue>, 'label'>
  )[],
  labels: Record<TValue, string>,
): SelectOptionType<TValue>[] =>
  values.map((value) =>
    typeof value === 'string'
      ? { label: labels[value], value }
      : {
          label: labels[value.value],
          ...value,
        },
  );
